"use client";

import * as React from "react";
import {
  IconDotsVertical,
  IconLoader2,
  IconSearch,
  IconTrash,
  IconUserCog,
  IconUserPlus,
} from "@tabler/icons-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InviteAgencyUsersDialog } from "@/features/agency/components";
import {
  useAgencyUsers,
  useRemoveAgencyUser,
  useUpdateAgencyUser,
} from "@/hooks/use-agencies";
import { isApiError } from "@/lib/axios";
import { AgencyUser, AgencyUserRole } from "@/types";

const PAGE_SIZE = 10;

const getUserDisplayName = (agencyUser: AgencyUser): string => {
  const { firstName, lastName } = agencyUser.user;

  if (!firstName && !lastName) {
    return "-";
  }

  return [firstName, lastName].filter(Boolean).join(" ");
};

const getUserDisplayNameOrEmail = (agencyUser: AgencyUser): string =>
  agencyUser.user.firstName && agencyUser.user.lastName
    ? getUserDisplayName(agencyUser)
    : agencyUser.user.email;

const getRoleLabel = (role: AgencyUserRole): string =>
  role === AgencyUserRole.ADMIN ? "Agency Admin" : "Agency Member";

/**
 * The team routes refuse to leave an agency without an active admin, with a
 * 409 `LAST_AGENCY_ADMIN` — branch on the code, not the message. Anything else
 * gets the caller's generic copy.
 */
const getAgencyUserErrorMessage = (error: unknown, fallback: string) =>
  isApiError(error) && error.code === "LAST_AGENCY_ADMIN"
    ? "An agency needs at least one active admin."
    : fallback;

/**
 * Invited and active read differently at a glance — an invitation can sit
 * unaccepted, and that has to be visible without opening anything.
 */
const getStatusBadge = (status: AgencyUser["status"]) => {
  const variants = {
    invited: "secondary",
    active: "default",
  } as const;

  return (
    <Badge variant={variants[status]} className="capitalize">
      {status}
    </Badge>
  );
};

interface AgencyPeopleTableProps {
  agencyId: string;
  agencyName: string;
  /**
   * Lifted so the detail page's "Total People" card can show the same number
   * without issuing a second read for it.
   */
  onTotalChange?: (total: number) => void;
  /**
   * Bumped by the page when its own "Invite People" button succeeds.
   *
   * The invite mutation has already invalidated the agencies key by then, so
   * the list refetches on its own; returning to the first page is the separate
   * thing that makes the new invitees visible from whichever page the admin
   * was on. That used to be a direct `setPageIndex(0)` when this table lived
   * inline on the page, and the signal is what preserves it now the page state
   * lives here.
   */
  resetPageSignal?: number;
}

/**
 * The people already added to an agency.
 *
 * Lifted out of `agency-detail-client.tsx` unchanged when the clients table
 * was added below it — two inline tables would have taken that page past 600
 * lines. Search and pagination are held here rather than in the URL, matching
 * how this table has always behaved.
 */
export function AgencyPeopleTable({
  agencyId,
  agencyName,
  onTotalChange,
  resetPageSignal,
}: AgencyPeopleTableProps) {
  const [pageIndex, setPageIndex] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const { data: usersData, isLoading: isLoadingUsers } = useAgencyUsers(
    agencyId,
    {
      pageNumber: pageIndex + 1, // API uses 1-based page numbers
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
    },
  );

  const updateAgencyUser = useUpdateAgencyUser();
  const removeAgencyUser = useRemoveAgencyUser();

  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<AgencyUser | null>(
    null,
  );
  const [selectedRole, setSelectedRole] = React.useState<AgencyUserRole>(
    AgencyUserRole.MEMBER,
  );
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = React.useState(false);
  const [userToRemove, setUserToRemove] = React.useState<AgencyUser | null>(
    null,
  );

  const users = usersData?.data ?? [];
  const totalUsers = usersData?.count ?? 0;

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPageIndex(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    onTotalChange?.(totalUsers);
  }, [totalUsers, onTotalChange]);

  React.useEffect(() => {
    if (resetPageSignal === undefined) return;
    setPageIndex(0);
  }, [resetPageSignal]);

  // The invite mutation already invalidated the agencies key; jumping back to
  // the first page is what makes the new invitees visible from any page.
  const handleInviteSuccess = () => {
    setPageIndex(0);
  };

  const handleOpenChangeRoleDialog = (agencyUser: AgencyUser) => {
    setSelectedUser(agencyUser);
    setSelectedRole(agencyUser.agencyUserRole);
    setChangeRoleDialogOpen(true);
  };

  const handleChangeRole = async () => {
    if (!selectedUser) return;

    try {
      await updateAgencyUser.mutateAsync({
        userId: selectedUser.user.id,
        data: { agencyUserRole: selectedRole },
      });
      toast.success("User role updated successfully");
      setChangeRoleDialogOpen(false);
    } catch (err) {
      console.error("Error updating agency user role:", err);
      toast.error(getAgencyUserErrorMessage(err, "Failed to update user role"));
    }
  };

  const handleOpenRemoveUserDialog = (agencyUser: AgencyUser) => {
    setUserToRemove(agencyUser);
    setRemoveUserDialogOpen(true);
  };

  const handleRemoveUser = async () => {
    if (!userToRemove) return;

    try {
      await removeAgencyUser.mutateAsync(userToRemove.user.id);
      toast.success("User removed from agency");

      // Removing the last row on a later page would leave it empty.
      if (users.length === 1 && pageIndex > 0) {
        setPageIndex((prev) => prev - 1);
      }

      setRemoveUserDialogOpen(false);
      setUserToRemove(null);
    } catch (err) {
      console.error("Error removing agency user:", err);
      toast.error(
        getAgencyUserErrorMessage(err, "Failed to remove user from agency"),
      );
    }
  };

  const isChangingRole = updateAgencyUser.isPending;
  const isRemovingUser = removeAgencyUser.isPending;

  const totalPages = Math.ceil(totalUsers / PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CardTitle>Agency People</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-semibold">
                {totalUsers} Total
              </Badge>
            </div>
          </div>
          <div className="relative w-full max-w-xs">
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search people in this agency"
              placeholder="Search people..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {users.length === 0 &&
        !isLoadingUsers &&
        totalUsers === 0 &&
        !debouncedSearch ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
            <p className="text-muted-foreground mb-4">
              No people found for this agency
            </p>
            <InviteAgencyUsersDialog
              agencyId={agencyId}
              agencyName={agencyName}
              onSuccess={handleInviteSuccess}
            >
              <Button variant="outline">
                <IconUserPlus className="mr-2 size-4" />
                Invite First Person
              </Button>
            </InviteAgencyUsersDialog>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="pl-6">Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px] pr-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingUsers ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        <div
                          role="status"
                          className="flex items-center justify-center"
                        >
                          <IconLoader2 className="mr-2 size-4 animate-spin" />
                          <span className="text-muted-foreground">
                            Loading people...
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-24 text-center text-muted-foreground"
                      >
                        No people found matching your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((agencyUser) => (
                      <TableRow key={agencyUser.id}>
                        <TableCell className="font-medium pl-6">
                          {getUserDisplayName(agencyUser)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {agencyUser.user.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {getRoleLabel(agencyUser.agencyUserRole)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(agencyUser.status)}
                        </TableCell>
                        <TableCell className="pr-6">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <IconDotsVertical className="size-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleOpenChangeRoleDialog(agencyUser)
                                }
                              >
                                <IconUserCog className="mr-2 size-4" />
                                Change Role
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() =>
                                  handleOpenRemoveUserDialog(agencyUser)
                                }
                              >
                                <IconTrash className="mr-2 size-4" />
                                Remove User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {totalUsers > 0 && (
              <div className="flex items-center justify-between px-2 mt-4">
                <div className="text-muted-foreground flex-1 text-sm">
                  Showing {users.length > 0 ? pageIndex * PAGE_SIZE + 1 : 0} to{" "}
                  {Math.min((pageIndex + 1) * PAGE_SIZE, totalUsers)} of{" "}
                  {totalUsers} people
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageIndex((prev) => prev - 1)}
                    disabled={pageIndex === 0 || isLoadingUsers}
                  >
                    Previous
                  </Button>
                  <div className="text-sm font-medium">
                    Page {pageIndex + 1} of {Math.max(1, totalPages)}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageIndex((prev) => prev + 1)}
                    disabled={pageIndex >= totalPages - 1 || isLoadingUsers}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Change Role Dialog */}
      <Dialog
        open={changeRoleDialogOpen}
        onOpenChange={(open) => {
          if (!isChangingRole) setChangeRoleDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for{" "}
              {selectedUser && getUserDisplayNameOrEmail(selectedUser)} in{" "}
              {agencyName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-4">
              <label
                htmlFor="change-agency-role-select"
                className="text-sm font-medium mb-3 block"
              >
                Select Role
              </label>
              <Select
                value={selectedRole}
                onValueChange={(value) =>
                  setSelectedRole(value as AgencyUserRole)
                }
                disabled={isChangingRole}
              >
                <SelectTrigger id="change-agency-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AgencyUserRole.ADMIN}>
                    Agency Admin
                  </SelectItem>
                  <SelectItem value={AgencyUserRole.MEMBER}>
                    Agency Member
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChangeRoleDialogOpen(false)}
              disabled={isChangingRole}
            >
              Cancel
            </Button>
            <Button onClick={handleChangeRole} disabled={isChangingRole}>
              {isChangingRole ? (
                <>
                  <IconLoader2 className="mr-2 size-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Role"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Dialog */}
      <Dialog
        open={removeUserDialogOpen}
        onOpenChange={(open) => {
          if (isRemovingUser) return;
          if (!open) setUserToRemove(null);
          setRemoveUserDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove User from Agency</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove{" "}
              {userToRemove && getUserDisplayNameOrEmail(userToRemove)} from{" "}
              {agencyName}? They will lose access to the agency. For someone
              who has not accepted yet, this withdraws their invitation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRemoveUserDialogOpen(false);
                setUserToRemove(null);
              }}
              disabled={isRemovingUser}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveUser}
              disabled={isRemovingUser}
            >
              {isRemovingUser ? (
                <>
                  <IconLoader2 className="mr-2 size-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove User"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
